// One save queue for the header and account screen. Pending edits are replayed over the last confirmed response,
// so a failed earlier save cannot undo a later edit (including one made by another hook instance).
import { PreferencesResponse, type DisplayPrefs } from '@aiax-hub/contracts';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import { useRef } from 'react';
import { useViewStore, type Lang, type Theme } from '../state/store';
import { authKeys } from './auth';
import { apiFetch } from './client';
import type { AuthMe } from './types';

export type Prefs = z.infer<typeof PreferencesResponse>;
export interface PrefsPatch { display?: Partial<DisplayPrefs>; notify?: Partial<Prefs['notify']> }
export const preferencesKey = ['me', 'preferences'] as const;
const endpoint = '/api/v1/me/preferences';
interface Edit { patch: PrefsPatch }
interface Saves {
  userId: string | undefined;
  base: Prefs;
  view: { lang: Lang; theme: Theme };
  pending: Edit[];
}
const saves = new WeakMap<QueryClient, Saves>();
const merge = (base: Prefs, patch: PrefsPatch): Prefs => ({ ...base, display: { ...base.display, ...patch.display }, notify: { ...base.notify, ...patch.notify } });
const sameAccount = (qc: QueryClient, state: Saves) => qc.getQueryData<AuthMe | null>(authKeys.me)?.user.id === state.userId;
function publish(qc: QueryClient, state: Saves) {
  if (!sameAccount(qc, state)) return;
  const prefs = state.pending.reduce((value, edit) => merge(value, edit.patch), state.base);
  const view = state.pending.reduce((value, { patch }) => ({
    lang: patch.display?.lang ?? value.lang, theme: patch.display?.theme ?? value.theme,
  }), state.view);
  qc.setQueryData(preferencesKey, prefs);
  qc.setQueryData<AuthMe | null>(authKeys.me, (me) => me ? { ...me, prefs: prefs.display } : me);
  useViewStore.getState().apply(view);
}

export const usePrefs = () => useQuery({ queryKey: preferencesKey, queryFn: ({ signal }) => apiFetch(endpoint, { schema: PreferencesResponse, signal }) });
export function useSavePrefs() {
  const qc = useQueryClient();
  const accounts = useRef(new WeakMap<PrefsPatch, string | undefined>());
  return useMutation({
    scope: { id: 'me.preferences' },
    mutationFn: (patch: PrefsPatch) => {
      if (accounts.current.get(patch) !== qc.getQueryData<AuthMe | null>(authKeys.me)?.user.id) throw new Error('Preference owner changed');
      return apiFetch(endpoint, { method: 'PUT', body: patch, schema: PreferencesResponse });
    },
    onMutate: (patch: PrefsPatch) => {
      void qc.cancelQueries({ queryKey: preferencesKey });
      void qc.cancelQueries({ queryKey: authKeys.me });
      const me = qc.getQueryData<AuthMe | null>(authKeys.me);
      accounts.current.set(patch, me?.user.id);
      let state = saves.get(qc);
      if (!state || state.userId !== me?.user.id) {
        const { lang, theme } = useViewStore.getState();
        state = { userId: me?.user.id, view: { lang, theme }, pending: [], base: qc.getQueryData<Prefs>(preferencesKey) ?? {
          display: me?.prefs ?? { lang, theme, numberUnit: 'compact', fontSize: 'm', wrapLongText: true, timezone: null },
          notify: { deviceDown: true, recordViewed: true, tokenExpiring: true, channel: 'app' }, availableChannels: ['app'],
        } };
        saves.set(qc, state);
      }
      const edit = { patch };
      state.pending.push(edit);
      publish(qc, state);
      return { state, edit };
    },
    onSuccess: (prefs, patch, ctx) => {
      ctx.state.base = prefs;
      if (patch.display?.lang) ctx.state.view.lang = prefs.display.lang;
      if (patch.display?.theme) ctx.state.view.theme = prefs.display.theme;
    },
    onSettled: (_data, _error, _patch, ctx) => {
      if (!ctx) return;
      ctx.state.pending = ctx.state.pending.filter((edit) => edit !== ctx.edit);
      publish(qc, ctx.state);
      if (!ctx.state.pending.length && saves.get(qc) === ctx.state) saves.delete(qc);
    },
  });
}
